import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import styles from './filterselect.module.scss';

export const FilterSelect = ({
  clearable = true,     // Display 'clear selection' X button
  filteringType,        // If equals: 'start' search left-to-right otherwise contains
  noResult,             // Text to display when empty list on search
  options = [],
  onChange = null,
  className = '',
  placeholder = '',
  preSelected = '',     // Pre-selected options value
  searchable = true,
  tabIndex = null,
  ...extraProps
}) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(preSelected);
  const [selectedText, setSelectedText] = useState('');
  const [selectedTextCurrent, setSelectedTextCurrent] = useState('');
  const refInputText = useRef(null);
  const refOptionIndex = useRef(null);
  const refOptionsList = useRef(null);
  const refSelectedElement = useRef(null);

  useEffect(() => {
    if (selectedValue) { // Set initial value if any
      const selectedText = options.find(opt => opt.value === selectedValue);
      if (selectedText) {
        setSelectedText(selectedText.text);
        setSelectedTextCurrent(selectedText.text);
      }
    }
  }, []);

  useEffect(() => {
    if ( // Select index 0 or currently selected on options update
      refOptionsList.current &&
      refOptionsList.current.children &&
      !refOptionsList.current.children[0].classList.contains(styles.choiceNoSelect)
    ) {
      refOptionIndex.current = 0;
      if (refSelectedElement.current) {
        refOptionIndex.current = Array.from(refOptionsList.current.children).indexOf(refSelectedElement.current);
      }
      refOptionsList.current.children[refOptionIndex.current].classList.add(styles.choiceActive);
    }
  }, [selectedText]);

  useEffect(() => {
    // Close FilterSelect on blur or outside click
    const closeListener = (e) => {
      if (!refInputText.current.contains(document.activeElement)) {
        setOpen(false);
        setSelectedText(selectedTextCurrent);
        refOptionIndex.current = null;
      }
    };

    if (refOptionsList.current) {
      refOptionsList.current.scrollIntoView({block: 'nearest'});
    }

    if (refSelectedElement.current) {
      refSelectedElement.current.scrollIntoView({block: 'nearest'});
    }
    if (refOptionsList.current) {
      refOptionIndex.current = Array.from(refOptionsList.current.children).indexOf(refSelectedElement.current);
      if (refOptionIndex.current === -1) {
        refOptionIndex.current = 0;
        refOptionsList.current.children[refOptionIndex.current].classList.add(styles.choiceActive);
      }
    }

    if (!open && selectedTextCurrent) {
      setSelectedText(selectedTextCurrent);
    }

    const handleKeyboard = (e) => {
      switch (e.keyCode) {
        case 27: { // ESC
          setOpen(false);
          setSelectedText(selectedTextCurrent);
          document.activeElement.blur();
          refOptionIndex.current = null;
          break;
        }
        case 40: { // Down
          if (!refOptionsList.current.children[0].classList.contains(styles.choiceNoSelect)) {
            e.preventDefault();
            if (!refOptionsList.current.classList.contains(styles.noPointerEvents)) {
              setPointerEvents(true);
            }

            const current = refOptionsList.current.children[refOptionIndex.current];
            if (current) {
              refOptionsList.current.children[refOptionIndex.current].classList.remove(styles.choiceActive);
            }
            refOptionIndex.current += 1;
            if ((refOptionIndex.current + 1) > refOptionsList.current.children.length) {
              refOptionIndex.current = 0;
            }

            const selectedElement = refOptionsList.current.children[refOptionIndex.current];
            selectedElement.classList.add(styles.choiceActive);
            selectedElement.scrollIntoView({block: 'nearest'});
          }
          break;
        }
        case 38: { // Up
          if (!refOptionsList.current.children[0].classList.contains(styles.choiceNoSelect)) {
            e.preventDefault();
            if (!refOptionsList.current.classList.contains(styles.noPointerEvents)) {
              setPointerEvents(true);
            }

            refOptionsList.current.classList.add(styles.noPointerEvents);

            refOptionsList.current.children[refOptionIndex.current].classList.remove(styles.choiceActive);
            refOptionIndex.current -= 1;
            if (refOptionIndex.current < 0) {
              refOptionIndex.current = refOptionsList.current.children.length - 1;
            }

            const selectedElement = refOptionsList.current.children[refOptionIndex.current];
            selectedElement.classList.add(styles.choiceActive);
            selectedElement.scrollIntoView({block: 'nearest'});
          }
          break;
        }
        case 13: { // Enter
          if (!refOptionsList.current.children[0].classList.contains(styles.choiceNoSelect)) {
            handleChange(e, true);
            setOpen(false);
            document.activeElement.blur();
          }
          break;
        }
        case 9: { // Tab
          setOpen(false);
          setSelectedText(selectedTextCurrent);
          refOptionIndex.current = null;
          break;
        }
        default: {}
      }
    };

    if (open) {
      window.addEventListener('blur', closeListener);
      window.addEventListener('click', closeListener);
      window.addEventListener('keydown', handleKeyboard);
    }

    return () => {
      window.removeEventListener('mousemove', initPointerEvents);
      window.removeEventListener('blur', closeListener);
      window.removeEventListener('click', closeListener);
      window.removeEventListener('keydown', handleKeyboard);
    };
  }, [open]);

  const initPointerEvents = () => setPointerEvents(false);
  const setPointerEvents = (active) => {
    if (active) {
      refOptionsList.current.classList.add(styles.noPointerEvents);
      window.addEventListener('mousemove', initPointerEvents);
    }
    if (!active) {
      refOptionsList.current.classList.remove(styles.noPointerEvents);
      window.removeEventListener('mousemove', initPointerEvents);
    }
  }

  const handleMouseMove = (e) => {
    let target = null;
    if (e.target.tagName === 'LI') {
      target = e.target;
    }
    if (target === null && e.relatedTarget.tagName === 'LI') {
      target = e.relatedTarget;
    }

    if (target !== null) {
      switch (e.type) {
        case 'mouseover': {
          if (!refOptionsList.current.children[0].classList.contains(styles.choiceNoSelect)) {
            const elem = refOptionsList.current.children[refOptionIndex.current];
            if (elem) {
              elem.classList.remove(styles.choiceActive);
              refOptionIndex.current = Array.from(refOptionsList.current.children).indexOf(target);
            }
  
            const hoverElem = refOptionsList.current.children[refOptionIndex.current]
            if (hoverElem) {
              hoverElem.classList.add(styles.choiceActive);
            }
          }
          break;
        }
        default: {}
      }
    }
  }

  const handleOpen = () => {
    setOpen(true);
    if (selectedValue) {
      const selectedText = options.find(opt => opt.value === selectedValue);
      if (selectedText) {
        setSelectedText(selectedText.text);
        setSelectedTextCurrent(selectedText.text);
      }
    } else {
      setSelectedTextCurrent('');  
    }
    setSelectedText('');
  };

  const handleChange = (e, update) => {
    const txt = e.target.dataset.option ?
      e.target.innerText :
      e.target.value;

    setSelectedText(txt);

    if (!update) {
      return;
    }

    const currentElem = refOptionsList.current.children[refOptionIndex.current];
    
    if (!currentElem) {
      return;
    }

    const val = currentElem.dataset.option;

    refOptionIndex.current = null;
    onChange(val);
    setSelectedTextCurrent(currentElem.innerText);
    setSelectedValue(val);
  };

  const handleClear = () => {
    onChange('');
    setSelectedValue('');
    setSelectedText('');
    setSelectedTextCurrent('');
  }

  const renderOptions = () => {
    let filteredOptions = [...options];
    if (searchable && selectedText) {
      if (filteringType.toLowerCase().indexOf('start') !== -1) {
        filteredOptions = filteredOptions.filter((opt) =>
          opt.text.toLowerCase().lastIndexOf(selectedText.toLowerCase(), 0) === 0
        );
      } else {
        filteredOptions = filteredOptions.filter((opt) =>
          opt.text.toLowerCase().includes(selectedText.toLowerCase())
        );
      }
    }

    if (!filteredOptions.length) {
      return (
        <li
          className={styles.choiceNoSelect}
          onClick={(e) => { e.stopPropagation(); }}
        >
          {noResult}
        </li>
      )
    }

    return filteredOptions.map((opt, index) => {
      const selected = opt.value === selectedValue;
      const selectedActive = selectedText !== '' || selectedText !== selectedTextCurrent;
      const classesOptions = cx(styles.choice, {
        [styles.choiceSelected]: selected,
        [styles.choiceActive]: selected && selectedActive,
      });

      return (
        <li
          className={classesOptions}
          data-option={opt.value}
          key={`${index}_${opt.text}`}
          ref={
            selected ?
              refSelectedElement:
              null
          }
        >
          {opt.text}
        </li>
      );
    });
  };

  const classes = cx(styles.filterSelect, {
    [className]: className,
    [styles.open]: open,
  });

  return (
    <div className={classes} data-cy="cc-filterSelect" {...extraProps}>
      <input
        className={styles.input}
        data-cy="cc-filterSelect--input"
        data-value={selectedValue}
        onChange={(e) => handleChange(e)}
        onFocus={handleOpen}
        placeholder={selectedTextCurrent || placeholder}
        readOnly={!searchable}
        ref={refInputText}
        tabIndex={tabIndex}
        type="text"
        value={selectedText}
      />
      {((clearable && !selectedValue) || (clearable && open) || !clearable) &&
        <span className={`${styles.icon} ${styles.arrow}`} />
      }
      {clearable && !open && selectedValue &&
        <button
          aria-label="clear"
          className={styles.clear}
          onClick={handleClear}
          role="button"
        />
      }
      {open && (
        <ul
          className={styles.choices}
          onClick={(e) => handleChange(e, true)}
          onMouseOver={handleMouseMove}
          onMouseOut={handleMouseMove}
          data-cy="cc-filterSelect--choices"
          ref={refOptionsList}
        >
          {renderOptions()}
        </ul>
      )}
    </div>
  );
};

FilterSelect.propTypes = {
  className: PropTypes.string,
  clearable: PropTypes.bool,
  filteringType: PropTypes.string,
  noResult: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  preSelected: PropTypes.string,
  searchable: PropTypes.bool,
  tabIndex: PropTypes.string,
};

export default FilterSelect;


